import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { chamarApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

import type { ListaDoCadastro } from '../parametrizacao/cadastros.contrato';
import { CHAVES as CHAVES_DOS_CADASTROS } from '../parametrizacao/cadastros.dados';
import type {
  DecisaoDaFila,
  FiltrosDaFila,
  ListaDaFila,
  PainelDaSincronizacao,
  PedidoDeAtualizacao,
  RecorteDaFonte,
  ResultadoDaDecisao,
} from './contrato';

/**
 * Camada de dados da sincronização com a fonte oficial e da fila de grafias.
 * Dado real, da API; quem pode ler e decidir (só papel administrativo) é
 * conferido no servidor e no banco (migração 0400).
 */
export const CAMINHO = '/bff/v1/admin/parametros/veiculos';
const CADASTROS = '/bff/v1/admin/parametros/cadastros';

export const TAMANHO_DA_PAGINA_DA_FILA = 20;

/** Enquanto há execução pedida ou em andamento, o painel se atualiza sozinho. */
export const INTERVALO_DE_ACOMPANHAMENTO_MS = 3000;

export const CHAVES = {
  tudo: ['admin', 'catalogo-veiculos'] as const,
  painel: ['admin', 'catalogo-veiculos', 'painel'] as const,
  filas: ['admin', 'catalogo-veiculos', 'fila'] as const,
  fila: (filtros: FiltrosDaFila) => ['admin', 'catalogo-veiculos', 'fila', filtros] as const,
  daMarca: (marcaId: string) => ['admin', 'catalogo-veiculos', 'da-marca', marcaId] as const,
};

export function consultaDaFila(filtros: FiltrosDaFila): string {
  const p = new URLSearchParams();
  p.set('pagina', String(filtros.pagina));
  p.set('tamanho', String(TAMANHO_DA_PAGINA_DA_FILA));
  if (filtros.busca.trim()) p.set('busca', filtros.busca.trim());
  if (filtros.pendencia !== 'todas') p.set('pendencia', filtros.pendencia);
  return p.toString();
}

export function usePainelDaSincronizacao() {
  return useQuery({
    queryKey: CHAVES.painel,
    queryFn: () => chamarApi<PainelDaSincronizacao>(`${CAMINHO}/sincronizacao`),
    retry: naoInsistirEmRecusa,
    refetchInterval: (consulta) =>
      consulta.state.data?.cargas.some(
        (c) => c.situacao === 'PEDIDA' || c.situacao === 'EM_ANDAMENTO',
      )
        ? INTERVALO_DE_ACOMPANHAMENTO_MS
        : false,
  });
}

export function useAtualizarAgora() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (fonte: string) =>
      chamarApi<PedidoDeAtualizacao>(
        `${CAMINHO}/sincronizacao/fontes/${encodeURIComponent(fonte)}/atualizar-agora`,
        { metodo: 'POST' },
      ),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVES.painel }),
  });
}

export function useAlterarRecorte() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ fonte, recorte }: { fonte: string; recorte: RecorteDaFonte }) =>
      chamarApi(`${CAMINHO}/sincronizacao/fontes/${encodeURIComponent(fonte)}`, {
        metodo: 'PUT',
        corpo: recorte,
      }),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVES.painel }),
  });
}

export function useFilaDeGrafias(filtros: FiltrosDaFila) {
  return useQuery({
    queryKey: CHAVES.fila(filtros),
    queryFn: () => chamarApi<ListaDaFila>(`${CAMINHO}/fila?${consultaDaFila(filtros)}`),
    placeholderData: keepPreviousData,
    retry: naoInsistirEmRecusa,
  });
}

/**
 * Modelos e versões ativos de uma marca, para escolher de quem o texto é
 * grafia (ou em que modelo vai a versão nova).
 */
export function useModelosEVersoesDaMarca(marcaId: string | null) {
  return useQuery({
    queryKey: CHAVES.daMarca(marcaId ?? ''),
    enabled: Boolean(marcaId),
    queryFn: async () => {
      const consulta = `marcaId=${encodeURIComponent(marcaId!)}&situacao=ativo&tamanho=100&ordenarPor=nome&sentido=asc`;
      const [modelos, versoes] = await Promise.all([
        chamarApi<ListaDoCadastro>(`${CADASTROS}/modelos-veiculo?${consulta}`),
        chamarApi<ListaDoCadastro>(`${CADASTROS}/versoes-veiculo?${consulta}`),
      ]);
      return { modelos: modelos.itens, versoes: versoes.itens };
    },
    retry: naoInsistirEmRecusa,
  });
}

/**
 * A decisão grava grafia e, quando é o caso, item novo: invalida a fila, o
 * painel e os cadastros de marca, modelo e versão.
 */
export function useDecidirDaFila() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decisao }: { id: string; decisao: DecisaoDaFila }) =>
      chamarApi<ResultadoDaDecisao>(`${CAMINHO}/fila/${encodeURIComponent(id)}/decidir`, {
        metodo: 'POST',
        corpo: decisao,
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVES.tudo });
      void cliente.invalidateQueries({ queryKey: CHAVES_DOS_CADASTROS.tudo });
    },
  });
}
